import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { FaSearch, FaSyncAlt, FaPrint, FaFileCsv } from "react-icons/fa";

const API_URL =
  process.env.REACT_APP_API_BASE_URL || "https://sipedasapi.bkkpangkalpinang.com/api";

export default function LogStokVaksinasiPage() {
  // ====== data source ======
  const [stokRows, setStokRows] = useState([]); // STOK VAKSIN
  const [logRows, setLogRows] = useState([]);   // LOG
  const [loadingStok, setLoadingStok] = useState(true);
  const [loadingLog, setLoadingLog] = useState(true);
  const [errStok, setErrStok] = useState("");
  const [errLog, setErrLog] = useState("");

  // ====== filter ======
  const [qStok, setQStok] = useState("");
  const [qLog, setQLog] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");

  // ====== fetcher ======
  const loadStok = async () => {
    setLoadingStok(true);
    setErrStok("");
    try {
      const { data } = await axios.get(`${API_URL}/stok-vaksins`);
      const list = Array.isArray(data?.data) ? data.data : [];
      const mapped = list.map((r) => ({
        id: r.id,
        namaVaksin: r.vaksin?.nama ?? r.vaksin ?? r.nama_vaksin ?? "-",
        harga:
          r.vaksin?.harga ??
          r.harga ??
          r.price ??
          null,
        wilker: r.lokasi?.nama ?? r.wilker ?? r.wilayah ?? r.lokasi ?? "-",
        stok: Number(r.stok_tersedia ?? r.stok ?? r.saldo ?? 0),
      }));
      setStokRows(mapped);
    } catch (e) {
      setErrStok(
        e?.response?.data?.message ||
          "Tidak dapat memuat data stok. Pastikan endpoint /stok-vaksins tersedia."
      );
      setStokRows([]);
    } finally {
      setLoadingStok(false);
    }
  };

  const loadLog = async () => {
    setLoadingLog(true);
    setErrLog("");
    try {
      const { data } = await axios.get(`${API_URL}/logs/stok-vaksinasi`, {
        params: { start, end },
      });
      const list = Array.isArray(data?.data) ? data.data : [];
      const mapped = list.map((r) => ({
        id: r.id,
        noRegistrasi: r.no_registrasi ?? r.no_reg ?? r.reg ?? "-",
        namaPasien: r.nama_pasien ?? r.pasien ?? r.nama ?? "-",
        vaksinDipilih: r.vaksin ?? r.nama_vaksin ?? "-",
        wilker: r.wilker ?? r.wilayah ?? r.lokasi ?? "-",
        tanggalLayanan: r.tanggal_layanan ?? r.tanggal ?? r.created_at ?? "-",
        status: r.status ?? r.keterangan ?? "-",
      }));
      setLogRows(mapped);
    } catch (e) {
      setErrLog(
        e?.response?.data?.message ||
          "Tidak dapat memuat log. Pastikan endpoint /logs/stok-vaksinasi tersedia."
      );
      setLogRows([]);
    } finally {
      setLoadingLog(false);
    }
  };

  useEffect(() => {
    loadStok();
    loadLog();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadLog();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [start, end]);

  // ====== filter client-side ======
  const stokFiltered = useMemo(() => {
    const n = (s) => String(s ?? "").toLowerCase();
    if (!qStok.trim()) return stokRows;
    const q = n(qStok);
    return stokRows.filter(
      (r) =>
        n(r.namaVaksin).includes(q) ||
        n(r.wilker).includes(q) ||
        n(r.harga).includes?.(q) // jika harga string
    );
  }, [stokRows, qStok]);

  const logFiltered = useMemo(() => {
    const n = (s) => String(s ?? "").toLowerCase();
    if (!qLog.trim()) return logRows;
    const q = n(qLog);
    return logRows.filter(
      (r) =>
        n(r.noRegistrasi).includes(q) ||
        n(r.namaPasien).includes(q) ||
        n(r.vaksinDipilih).includes(q) ||
        n(r.wilker).includes(q) ||
        n(r.tanggalLayanan).includes(q) ||
        n(r.status).includes(q)
    );
  }, [logRows, qLog]);

  // ====== util ======
  const formatNum = (v) => Number(v || 0).toLocaleString("id-ID");
  const formatIDR = (v) =>
    new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", maximumFractionDigits: 0 }).format(
      Number(v || 0)
    );
  const formatDate = (s) => {
    if (!s) return "-";
    const d = new Date(s);
    if (!isNaN(d)) {
      return d.toLocaleDateString("id-ID", {
        year: "numeric",
        month: "short",
        day: "2-digit",
      });
    }
    return String(s).slice(0, 10);
  };
  const badgeClass = (status) => {
    const t = String(status ?? "").toLowerCase();
    if (t.includes("diterima") || t.includes("lunas") || t.includes("approved")) return "ok";
    if (t.includes("pending") || t.includes("menunggu")) return "warn";
    if (t.includes("ditolak") || t.includes("batal") || t.includes("rejected")) return "bad";
    return "neutral";
  };
  const exportCSV = (rows, headers, filename) => {
    const lines = [headers.join(",")];
    rows.forEach((r) => {
      const vals = headers.map((h) =>
        String(r[h] ?? "").replaceAll("\n", " ").replaceAll(",", " ")
      );
      lines.push(vals.join(","));
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <style>{styles}</style>

      <div className="page-title">DAFTAR STOK VAKSIN & PENGAJUAN</div>

      {/* ====== STOK VAKSIN ====== */}
      <div className="card">
        <div className="section-title">STOK VAKSIN</div>

        <div className="toolbar">
          <div className="search">
            <FaSearch />
            <input
              value={qStok}
              onChange={(e) => setQStok(e.target.value)}
              placeholder="Cari nama vaksin atau wilayah kerja…"
            />
          </div>
          <div className="actions">
            <button className="btn ghost" onClick={loadStok}>
              <FaSyncAlt /> Refresh
            </button>
            <button
              className="btn ghost"
              onClick={() =>
                exportCSV(
                  stokFiltered.map((r, i) => ({
                    Nomor: i + 1,
                    "Nama Vaksin": r.namaVaksin,
                    HARGA: r.harga ?? 0,
                    "Wilayah Kerja": r.wilker,
                    "Stok Vaksin": r.stok,
                  })),
                  ["Nomor", "Nama Vaksin", "HARGA", "Wilayah Kerja", "Stok Vaksin"],
                  `stok_vaksin_${new Date().toISOString().slice(0, 10)}.csv`
                )
              }
            >
              <FaFileCsv /> Export
            </button>
            <button className="btn primary" onClick={() => window.print()}>
              <FaPrint /> Print
            </button>
          </div>
        </div>

        <div className="table-wrap printable">
          <table className="table">
            <thead>
              <tr>
                <th>Nomor</th>
                <th>Nama Vaksin</th>
                <th>HARGA</th>
                <th>Wilayah Kerja</th>
                <th>Stok Vaksin</th>
              </tr>
            </thead>
            <tbody>
              {loadingStok ? (
                <SkeletonRows cols={5} />
              ) : stokFiltered.length ? (
                stokFiltered.map((r, i) => (
                  <tr key={r.id ?? i}>
                    <td className="center">{i + 1}</td>
                    <td className="bold">{r.namaVaksin}</td>
                    <td className="right">{r.harga == null ? "-" : formatIDR(r.harga)}</td>
                    <td>{r.wilker}</td>
                    <td className="right">{formatNum(r.stok)}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="empty">
                    {errStok || "Tidak ada data."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ====== LOG ====== */}
      <div className="card">
        <div className="section-title">LOG</div>

        <div className="toolbar">
          <div className="date-range">
            <label>Mulai</label>
            <input type="date" value={start} onChange={(e) => setStart(e.target.value)} />
            <label>Sampai</label>
            <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} />
          </div>

          <div className="search">
            <FaSearch />
            <input
              value={qLog}
              onChange={(e) => setQLog(e.target.value)}
              placeholder="Cari registrasi, pasien, vaksin, wilker, status…"
            />
          </div>

          <div className="actions">
            <button className="btn ghost" onClick={loadLog}>
              <FaSyncAlt /> Refresh
            </button>
            <button
              className="btn ghost"
              onClick={() =>
                exportCSV(
                  logFiltered.map((r, i) => ({
                    "No.": i + 1,
                    "No. Registrasi": r.noRegistrasi,
                    "Nama Pasien": r.namaPasien,
                    "Vaksin yang dipilih": r.vaksinDipilih,
                    Wilker: r.wilker,
                    "Tanggal Layanan": r.tanggalLayanan,
                    Status: r.status,
                  })),
                  [
                    "No.",
                    "No. Registrasi",
                    "Nama Pasien",
                    "Vaksin yang dipilih",
                    "Wilker",
                    "Tanggal Layanan",
                    "Status",
                  ],
                  `log_stok_vaksin_${new Date().toISOString().slice(0, 10)}.csv`
                )
              }
            >
              <FaFileCsv /> Export
            </button>
            <button className="btn primary" onClick={() => window.print()}>
              <FaPrint /> Print
            </button>
          </div>
        </div>

        <div className="table-wrap printable">
          <table className="table">
            <thead>
              <tr>
                <th>No.</th>
                <th>No. Registrasi</th>
                <th>Nama Pasien</th>
                <th>Vaksin yang dipilih</th>
                <th>Wilker</th>
                <th>Tanggal Layanan</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {loadingLog ? (
                <SkeletonRows cols={7} />
              ) : logFiltered.length ? (
                logFiltered.map((r, i) => (
                  <tr key={r.id ?? i}>
                    <td className="center">{i + 1}</td>
                    <td className="mono">{r.noRegistrasi}</td>
                    <td>{r.namaPasien}</td>
                    <td>{r.vaksinDipilih}</td>
                    <td>{r.wilker}</td>
                    <td>{formatDate(r.tanggalLayanan)}</td>
                    <td>
                      <span className={`badge ${badgeClass(r.status)}`}>
                        {r.status ?? "-"}
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7} className="empty">
                    {errLog || "Tidak ada data."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

/* helpers */
function SkeletonRows({ cols = 4, rows = 4 }) {
  return (
    <>
      {Array.from({ length: rows }).map((_, i) => (
        <tr key={`sk-${i}`} className="skeleton-row">
          {Array.from({ length: cols }).map((__, j) => (
            <td key={`sk-${i}-${j}`} />
          ))}
        </tr>
      ))}
    </>
  );
}

const styles = `
.page-title{
  text-align:center; font-weight:900; font-size:24px; margin: 0 0 14px; letter-spacing:.02em;
}
.card{
  background:#fff; border-radius:14px; padding:14px; box-shadow:0 6px 18px rgba(0,0,0,.06); margin-bottom:16px;
}
.section-title{ font-weight:800; letter-spacing:.02em; margin-bottom:10px; }
.toolbar{ display:grid; grid-template-columns: 1fr 1fr auto; gap:10px; align-items:center; margin-bottom:10px; }
@media(max-width:900px){ .toolbar{ grid-template-columns:1fr; } }
.search{ display:flex; align-items:center; gap:8px; border:1px solid #e2e8f0; border-radius:10px; padding:8px 10px; }
.search input{ border:none; outline:none; width:100%; }
.date-range{ display:flex; align-items:center; gap:8px; }
.date-range input{ border:1px solid #e2e8f0; border-radius:10px; padding:8px 10px; }
.actions{ display:flex; gap:8px; justify-content:flex-end; flex-wrap:wrap; }
.btn{ display:inline-flex; align-items:center; gap:8px; padding:8px 12px; border-radius:10px; border:1px solid #cfe3de; background:#fff; cursor:pointer; font-weight:700; }
.btn.ghost{ color:#0f766e; }
.btn.primary{ background:#10b981; color:#fff; border-color:#10b981; }
.table-wrap{ overflow:auto; border:1px solid #e7efed; border-radius:12px; }
.table{ width:100%; border-collapse:separate; border-spacing:0; }
.table thead th{ background:#cfe3f7; color:#0f172a; font-weight:800; padding:10px 12px; white-space:nowrap; border-bottom:1px solid #e2e8f0; text-align:left; }
.table tbody td{ padding:10px 12px; border-bottom:1px solid #f1f5f4; }
.center{ text-align:center; } .right{ text-align:right; } .bold{ font-weight:700; } .mono{ font-family:ui-monospace,Menlo,Consolas,monospace; }
.empty{ text-align:center; padding:18px; color:#64748b; }
.badge{ display:inline-flex; padding:4px 10px; border-radius:999px; font-weight:800; }
.badge.ok{ background:#ecfdf5; color:#047857; }
.badge.warn{ background:#fff7ed; color:#b45309; }
.badge.bad{ background:#fef2f2; color:#b91c1c; }
.badge.neutral{ background:#f1f5f9; color:#334155; }
.skeleton-row td::after{ content:""; display:block; height:10px; border-radius:6px; background:linear-gradient(90deg,#f3f7f6,#eaf3f1,#f3f7f6); animation:pulse 1.2s infinite; }
@keyframes pulse{ 0%{opacity:.7} 50%{opacity:1} 100%{opacity:.7} }
@media print{ .toolbar{ display:none !important; } .card{ box-shadow:none; border:0; } }
`;
